// components/InvitationMusic.js
//
// The invitation's song (shared/invitationMusic.js), in YouTube's own player,
// as the last section of the invitation page - and InvitationMusicRow, the
// song's name and a Play / Pause button under the venue, so a guest can start
// or stop it without scrolling down.
//
// The player is visible, at least 200 px tall, and nothing is drawn on it. It
// starts by itself as the page loads (AUTOPLAY_ON_LOAD - see the note there on
// YouTube's rule about automatic playback), or with that off, once more than
// half of it is on screen. A tap on Play starts it wherever it is.
//
// Sound: the song plays audibly or not at all - never muted. Browsers refuse
// to start sound before the visitor has touched the page, so where that is
// the case (a guest opening a shared link, every time) it waits: the row says
// "Tap anywhere to play", and their first tap, anywhere on the page, starts
// it with sound. Where sound is already allowed (they have tapped on the
// site, or the browser trusts it) it starts as the page loads. Not
// autoplayed in the create preview.
//
// The page owns a controller (useInvitationMusicController) that the card
// fills in and the row reads, so the button always acts on the real player and
// shows its real state - YouTube's, not a guess of ours.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiMusic, FiPlay, FiPause } from 'react-icons/fi';
import { AUTOPLAY_ON_LOAD, AUTOPLAY_VISIBLE, INVITATION_MUSIC_LABEL, cleanVideoId } from '../shared/invitationMusic';

let apiPromise = null;
/** YouTube's IFrame Player API, loaded once per page. */
function loadYouTubeApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!apiPromise) {
    apiPromise = new Promise((resolve, reject) => {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previous === 'function') previous();
        resolve(window.YT);
      };
      const s = document.createElement('script');
      s.src = 'https://www.youtube.com/iframe_api';
      s.async = true;
      s.onerror = () => { apiPromise = null; reject(new Error('YouTube could not load')); };
      document.head.appendChild(s);
    });
  }
  return apiPromise;
}

/** Whether the visitor has touched the page, so sound is allowed. */
const hasUserActivation = () => !!navigator.userActivation?.hasBeenActive;

// YT.PlayerState values, so the toggle can read them before the API object
// is at hand.
const PLAYING = 1;
const BUFFERING = 3;

/** The page's handle on its music: the card fills it, the row uses it. */
export function useInvitationMusicController() {
  const playerRef = useRef(null);
  // waiting: it should be playing, and the browser is holding it for a tap.
  const [status, setStatus] = useState({ ready: false, playing: false, waiting: false });

  /** Play if it is not playing, pause if it is - asking YouTube which. */
  const toggle = useCallback(() => {
    const player = playerRef.current;
    if (!player?.getPlayerState) return;
    const state = player.getPlayerState();
    if (state === PLAYING || state === BUFFERING) {
      player.pauseVideo();
    } else {
      player.unMute();
      player.setVolume?.(100);
      setStatus((s) => ({ ...s, waiting: false }));
      player.playVideo();
    }
  }, []);

  return { playerRef, status, setStatus, toggle };
}

/** The song's name and Play / Pause, for under the venue. */
export function InvitationMusicRow({ music, controller, color, mutedColor, fontStyle }) {
  if (!cleanVideoId(music?.videoId)) return null;
  const { status, toggle } = controller;
  return (
    <div className="ed-row invitation-music-row" style={{ color }}>
      <FiMusic size={18} aria-hidden="true" />
      <span className="ed-row-text invitation-music-row-text" style={fontStyle}>
        {status.waiting && !status.playing
          ? <span style={{ color: mutedColor }}>Tap anywhere to play</span>
          : `${music.title}${music.channel ? ` · ${music.channel}` : ''}`}
      </span>
      <button
        type="button"
        className="invitation-music-toggle"
        onClick={toggle}
        disabled={!status.ready}
        aria-label={status.playing ? 'Pause the invitation music' : 'Play the invitation music'}
      >
        {status.playing ? <FiPause size={16} /> : <FiPlay size={16} />}
      </button>
    </div>
  );
}

/** The player itself, as the last section. */
export default function InvitationMusic({ music, controller, autoplay = true, textColor }) {
  const slotRef = useRef(null);
  const startedRef = useRef(false);
  const { playerRef, status, setStatus } = controller;
  const videoId = cleanVideoId(music?.videoId);

  // The player itself. When it is to start as the page opens, it is created
  // set to play - with sound, never muted - rather than told to play once
  // loaded: on iPhones that is what YouTube's player reliably honours.
  useEffect(() => {
    if (!videoId || !slotRef.current) return undefined;
    let cancelled = false;
    const startOnLoad = autoplay && AUTOPLAY_ON_LOAD;
const mount = document.createElement('div');
    slotRef.current.appendChild(mount);
    loadYouTubeApi().then((YT) => {
      if (cancelled) return;
      playerRef.current = new YT.Player(mount, {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          playsinline: 1, rel: 0, loop: 1, playlist: videoId, controls: 1,
          modestbranding: 1, origin: window.location.origin,
          autoplay: startOnLoad ? 1 : 0,
          mute: 0,
        },
        events: {
          onReady: () => { if (!cancelled) setStatus((s) => ({ ...s, ready: true })); },
          onStateChange: (e) => {
            if (cancelled) return;
            const player = playerRef.current;
            const on = e.data === YT.PlayerState.PLAYING || e.data === YT.PlayerState.BUFFERING;
            // A browser that refuses sound may let it start muted instead.
            // Silent is not what the host chose: stop, and wait for a tap.
            if (on && !hasUserActivation() && player?.isMuted?.()) {
              player.pauseVideo();
              player.unMute();
              setStatus((s) => ({ ...s, playing: false, waiting: true }));
              return;
            }
            // Whatever started or stopped it - our button, YouTube's own
            // controls, the end of the video - the row shows the truth.
            const off = e.data === YT.PlayerState.PAUSED || e.data === YT.PlayerState.ENDED;
            if (on) setStatus((s) => ({ ...s, playing: true, waiting: false }));
            else if (off) setStatus((s) => ({ ...s, playing: false }));
            if (on) startedRef.current = true;
          },
        },
      });
    }).catch(() => {});
    return () => {
      cancelled = true;
      try { playerRef.current?.destroy(); } catch { /* already gone */ }
      playerRef.current = null;
      setStatus({ ready: false, playing: false, waiting: false });
      mount.remove();
    };
  }, [videoId, autoplay, playerRef, setStatus]);

  // Starts by itself: as soon as the player is ready (AUTOPLAY_ON_LOAD), or
  // else once more than half of it is on screen - YouTube's own rule - unless
  // the guest already started it from the row. With sound: where the browser
  // has not seen a tap yet, it waits for one (below).
  useEffect(() => {
    if (!status.ready || !autoplay || !slotRef.current) return undefined;
    const start = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const player = playerRef.current;
      if (!player) return;
      if (!hasUserActivation()) {
        setStatus((s) => ({ ...s, waiting: true }));
        return;
      }
      player.unMute();
      player.playVideo();
    };
    if (AUTOPLAY_ON_LOAD) {
      start();
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry?.isIntersecting || entry.intersectionRatio < AUTOPLAY_VISIBLE) return;
      observer.disconnect();
      start();
    }, { threshold: [0, AUTOPLAY_VISIBLE, 1] });
    observer.observe(slotRef.current);
    return () => observer.disconnect();
  }, [status.ready, autoplay, playerRef, setStatus]);

  // Waiting for a tap: their first touch anywhere starts it, with sound. (A
  // tap on our own Play button does the same through toggle.) It keeps
  // listening until the song is actually playing: a touch that only scrolls
  // may not count as permission, and the tap after it should still start it.
  // "waiting" ends when YouTube reports playing (onStateChange).
  useEffect(() => {
    if (!status.waiting || status.playing) return undefined;
    const startWithSound = (e) => {
      if (e?.target?.closest?.('.invitation-music-toggle')) return;
      const player = playerRef.current;
      if (!player) return;
      player.unMute();
      player.setVolume?.(100);
      player.playVideo();
    };
    const opts = { capture: true };
    window.addEventListener('pointerup', startWithSound, opts);
    window.addEventListener('keydown', startWithSound, opts);
    window.addEventListener('touchend', startWithSound, opts);
    return () => {
      window.removeEventListener('pointerup', startWithSound, opts);
      window.removeEventListener('keydown', startWithSound, opts);
      window.removeEventListener('touchend', startWithSound, opts);
    };
  }, [status.waiting, status.playing, playerRef, setStatus]);

  if (!videoId) return null;

  return (
    <div className="invitation-music">
      <div className="invitation-music-head">
        <FiMusic size={18} aria-hidden="true" style={{ color: textColor }} />
        <strong style={{ color: textColor }}>{INVITATION_MUSIC_LABEL}</strong>
      </div>
      <div ref={slotRef} className="invitation-music-player" />
    </div>
  );
}
